import { ErrorMessage } from '@hookform/error-message';
import { useState } from 'react';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import Button from '@/components/elements/Button/BaseButton';
import Textarea from '@/components/elements/Form/Textarea';
import ErrorModal from '@/components/layouts/Modal/Error';
import {
  EntryRacerNote,
  useSaveEntryRacerNoteMutation,
} from '@/lib/apollo/types/gen/graphql';

import { Buttons } from '../style';
import { Description, ValdMsg } from './style';

export enum Display {
  List,
  Create,
  Edit,
  Delete,
}

interface EntryRacerNoteFormProps {
  raceId: string;
  registrationNum: number;
  setEntryRacerNoteList: React.Dispatch<React.SetStateAction<EntryRacerNote[]>>;
  targetNote: EntryRacerNote;
  display: Display;
}

interface FormInputs {
  body: string;
}

const EntryRacerNoteForm: React.FC<EntryRacerNoteFormProps> = ({
  raceId,
  registrationNum,
  setEntryRacerNoteList,
  targetNote,
  display,
}) => {
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [saveEntryRacerNoteMutation, saveEntryRacerNoteMutationResult] =
    useSaveEntryRacerNoteMutation();
  const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<FormInputs>({
    mode: 'onChange',
    defaultValues: {
      body: display === Display.Create ? '' : targetNote.body,
    },
  });

  const onSubmit = (data: FormInputs) => {
    if (isProcessing) return;
    setIsProcessing(true);
    saveEntryRacerNoteMutation({
      variables: {
        raceID: display === Display.Create ? raceId : targetNote.raceId,
        registrationNum,
        body: data.body,
      },
    })
      .then(res => {
        const newEntryRacerNote = { ...res.data!.saveEntryRacerNote };
        setEntryRacerNoteList(prev => {
          const temp = [...prev] as EntryRacerNote[];
          if (display === Display.Create) {
            temp.push(newEntryRacerNote as EntryRacerNote);
            temp.sort((a, b) => {
              return a.heldDate.isBefore(b.heldDate) ? 1 : -1;
            });
          } else {
            const index = temp.findIndex(
              t =>
                t.registrationNum === registrationNum &&
                t.raceId === targetNote.raceId,
            );
            temp[index] = newEntryRacerNote as EntryRacerNote;
          }
          return temp;
        });
      })
      .catch(() => {
        setIsOpenErrorModal(true);
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Textarea
        height={['226px', '226px', '100px', '100px']}
        hasError={errors.body?.type === 'maxLength'}
        {...register('body', {
          required: true,
          maxLength: {
            value: 300,
            message: '※300文字以内で入力してください。',
          },
        })}
      />
      <Description>
        <ErrorMessage
          errors={errors}
          name="body"
          render={({ message }) => <ValdMsg>{message}</ValdMsg>}
        />
        <p>1文字以上300文字以内で入力してください。</p>
      </Description>
      <Buttons>
        <Button
          type="submit"
          size="m"
          bgColor="primary"
          fontWeight="bold"
          height="56px"
          radius="l"
          outline
          disabled={!isValid}
        >
          メモを追加する
        </Button>
      </Buttons>
      <ErrorModal
        isOpen={isOpenErrorModal}
        handleClose={() => setIsOpenErrorModal(false)}
        apolloError={saveEntryRacerNoteMutationResult.error}
      />
    </form>
  );
};

export default EntryRacerNoteForm;
